// src/feature-module/settings/Dashboard/VehculeBooking.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ApexCharts, { ApexOptions } from "apexcharts";
import apiDefinitions from "../../../core/api/apiDefinitions";
import { showError } from "../../../core/alert/sweetAlertConfig";

type RangeKey = "30d" | "15d" | "7d";

interface BookingStageCounts {
  pendingBookings: number;
  approvedBookings: number;
  rejectedBookings: number;
  finalApprovedBookings: number;
  finalRejectedBookings: number;
  ongoingBookings: number;
  completedBookings: number;
  cancelledBookings: number;
  totalBookings: number;
  date?: string;
}

const STAGE_LABELS: Array<keyof BookingStageCounts> = [
  "pendingBookings",
  "approvedBookings",
  "finalApprovedBookings",
  "ongoingBookings",
  "completedBookings",
  "rejectedBookings",
  "cancelledBookings",
];

const STAGE_TITLES: Record<string, string> = {
  pendingBookings: "Pending",
  approvedBookings: "Approved",
  finalApprovedBookings: "Final Approved",
  ongoingBookings: "Ongoing",
  completedBookings: "Completed",
  rejectedBookings: "Rejected",
  cancelledBookings: "Cancelled",
};

const MODERN_STAGE_COLORS = [
  "#F59E0B", // Pending (Amber)
  "#3B82F6", // Approved (Blue)
  "#10B981", // Final Approved (Emerald)
  "#06B6D4", // Ongoing (Cyan)
  "#059669", // Completed (Green)
  "#EF4444", // Rejected (Red)
  "#64748B", // Cancelled (Slate)
];

const VehicleBooking: React.FC = () => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<ApexCharts | null>(null);

  const [range, setRange] = useState<RangeKey>("30d");
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<BookingStageCounts | null>(null);

  const fetchCounts = async (r: RangeKey) => {
    setLoading(true);
    try {
      const response = await apiDefinitions.getBookingDashboardCounts();
      if (response?.data?.status === "S1000") {
        setData(response.data.data as BookingStageCounts);
      } else {
        showError(
          "Failed to fetch booking counts: " +
            (response?.data?.statusDescription || "Unknown error")
        );
      }
    } catch (e: any) {
      showError(
        "Failed to fetch booking counts: " +
          (e?.response?.data?.statusDescription || "Network error")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCounts(range);
  }, [range]);

  const { series, labels, total } = useMemo(() => {
    if (!data) {
      return { series: [] as number[], labels: [] as string[], total: 0 };
    }
    const values = STAGE_LABELS.map((key) => Number((data as any)[key] || 0));
    const totalBookings = Number(data.totalBookings || 0);
    const readableLabels = STAGE_LABELS.map((k) => STAGE_TITLES[k]);
    return { series: values, labels: readableLabels, total: totalBookings };
  }, [data]);

  useEffect(() => {
    if (!chartRef.current) return;

    const options: ApexOptions = {
      chart: {
        type: "donut",
        height: 300,
        animations: {
          enabled: true,
          dynamicAnimation: { speed: 800 },
        },
        toolbar: { show: false },
      },
      labels,
      series,
      colors: MODERN_STAGE_COLORS,
      stroke: {
        width: 3,
        colors: ["#ffffff"],
      },
      legend: {
        show: true,
        position: "bottom",
        horizontalAlign: "center",
        fontSize: "12px",
        fontFamily: "inherit",
        markers: { shape: "circle", size: 6 },
        itemMargin: { horizontal: 8, vertical: 4 },
      },
      dataLabels: {
        enabled: true,
        formatter: (val: number) => (val > 4 ? `${Math.round(val)}%` : ""),
      },
      tooltip: {
        y: {
          formatter: (count: number) => {
            const pct = total > 0 ? ((count / total) * 100).toFixed(1) : "0.0";
            return `${count} Bookings (${pct}%)`;
          },
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "68%",
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: "13px",
                fontWeight: 600,
                color: "#64748B",
              },
              value: {
                show: true,
                fontSize: "24px",
                fontWeight: 800,
                color: "#0F172A",
                formatter: (val: string) => String(val),
              },
              total: {
                show: true,
                label: "Total Bookings",
                color: "#64748B",
                fontWeight: 600,
                formatter: () => String(total || 0),
              },
            },
          },
        },
      },
      noData: {
        text: loading ? "Loading Bookings..." : "No Booking Data",
        style: { color: "#94A3B8", fontSize: "14px" },
      },
      responsive: [
        {
          breakpoint: 576,
          options: {
            chart: { height: 260 },
            legend: { position: "bottom" },
          },
        },
      ],
    };

    if (!chartInstance.current) {
      chartInstance.current = new ApexCharts(chartRef.current, options);
      chartInstance.current.render();
    } else {
      chartInstance.current.updateOptions(options, false, true);
      chartInstance.current.updateSeries(series, true);
    }
  }, [labels, series, total, loading]);

  useEffect(() => {
    return () => {
      chartInstance.current?.destroy();
      chartInstance.current = null;
    };
  }, []);

  return (
    <div className="rm-glass-card rm-glass-card-interactive flex-fill w-100 p-3 p-md-4">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 pb-3 mb-3 border-bottom border-light">
        <div className="d-flex align-items-center gap-2">
          <div
            className="d-flex align-items-center justify-content-center rounded-circle"
            style={{
              width: "42px",
              height: "42px",
              background: "var(--rm-primary-light)",
              color: "var(--rm-primary)",
              fontSize: "1.25rem",
            }}
          >
            <i className="ti ti-car" />
          </div>
          <div>
            <h5 className="m-0 fw-bold" style={{ color: "var(--rm-text-primary)" }}>
              Vehicle Bookings
            </h5>
            <small style={{ color: "var(--rm-text-muted)" }}>
              {loading
                ? "Updating chart..."
                : `Total: ${total} Bookings ${
                    data?.date ? `• ${new Date(data.date).toLocaleDateString()}` : ""
                  }`}
            </small>
          </div>
        </div>

        {/* Range Selector & Refresh */}
        <div className="d-flex align-items-center gap-2">
          <div className="btn-group btn-group-sm p-1 rounded-pill bg-light border">
            {(["7d", "15d", "30d"] as RangeKey[]).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRange(r)}
                className={`btn btn-sm rounded-pill px-2 py-0 border-0 ${
                  range === r ? "btn-primary shadow-sm" : "btn-light text-muted"
                }`}
                style={{ fontSize: "0.75rem", fontWeight: 600 }}
              >
                {r.toUpperCase()}
              </button>
            ))}
          </div>

          <button
            onClick={() => fetchCounts(range)}
            disabled={loading}
            className="btn btn-sm btn-light rounded-pill px-3 shadow-sm border-0 d-inline-flex align-items-center gap-1"
            style={{ fontSize: "0.8rem", color: "var(--rm-text-secondary)" }}
            title="Refresh Chart"
          >
            <i className={`ti ti-refresh ${loading ? "rotate" : ""}`} />
          </button>
        </div>
      </div>

      {/* Chart */}
      <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "300px" }}>
        <div id="vehicle-bookings-chart" ref={chartRef} className="w-100" />
      </div>

      {/* Quick Stage Metric Chips */}
      {data && (
        <div className="row g-2 mt-2 pt-2 border-top border-light text-center">
          <div className="col-4 col-sm">
            <small className="text-muted d-block" style={{ fontSize: "0.7rem" }}>Ongoing</small>
            <span className="fw-bold" style={{ color: "var(--rm-secondary)", fontSize: "0.95rem" }}>
              {data.ongoingBookings || 0}
            </span>
          </div>
          <div className="col-4 col-sm">
            <small className="text-muted d-block" style={{ fontSize: "0.7rem" }}>Completed</small>
            <span className="fw-bold text-success" style={{ fontSize: "0.95rem" }}>
              {data.completedBookings || 0}
            </span>
          </div>
          <div className="col-4 col-sm">
            <small className="text-muted d-block" style={{ fontSize: "0.7rem" }}>Approved</small>
            <span className="fw-bold" style={{ color: "var(--rm-primary)", fontSize: "0.95rem" }}>
              {(data.approvedBookings || 0) + (data.finalApprovedBookings || 0)}
            </span>
          </div>
          <div className="col-6 col-sm">
            <small className="text-muted d-block" style={{ fontSize: "0.7rem" }}>Pending</small>
            <span className="fw-bold text-warning" style={{ fontSize: "0.95rem" }}>
              {data.pendingBookings || 0}
            </span>
          </div>
          <div className="col-6 col-sm">
            <small className="text-muted d-block" style={{ fontSize: "0.7rem" }}>Rejected</small>
            <span className="fw-bold text-danger" style={{ fontSize: "0.95rem" }}>
              {(data.rejectedBookings || 0) + (data.finalRejectedBookings || 0)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleBooking;
